import { api } from "@/lib/api";

/**
 * The knowledge base (BL-002).
 *
 * ⚠️ **Reading needs no permission, and that is deliberate.** A `kb.read` would have to be
 * seeded onto `Requester`, which every employee holds, and a permission everybody has says
 * nothing. What decides reach is the ROLLOUT — `canSeeKnowledgeBase` on the session — plus, per
 * article, the query filter and `security.KnowledgeAccessPolicy`. Every route answers **404**
 * when the feature is off or the rollout does not reach the caller; never 403, which would
 * confirm to somebody probing that a library exists and that they are outside it.
 *
 * ⚠️ **`body` is MARKDOWN and is never HTML.** `lib/article-markdown.ts` parses it into a typed
 * tree and `ArticleBody` draws elements — nothing in this product turns author text into
 * markup, which is why the ban on `dangerouslySetInnerHTML` and the enforcing CSP need no
 * sanitiser behind them.
 */

/** How an article reads — its template, and Learn's content-type idea. */
export type ArticleContentType = "HowTo" | "Troubleshooting" | "Reference";

export type ArticleStatus = "Draft" | "InReview" | "Published" | "Archived";

/** Who a PUBLISHED article reaches. `Everyone` is the default and nearly every article. */
export type ArticleVisibility = "Everyone" | "Audience" | "DeskOnly";

/**
 * ⚠️ No `Site`. An audience is enforced in both layers and the SQL one can only read what
 * `SESSION_CONTEXT` carries — there is no site there. Offering a site audience enforced in EF
 * alone is the single layer that has twice been caught failing silently in production.
 */
export type ArticleAudienceKind = "User" | "Team";

/** How far the knowledge base has been rolled out. Not a permission — an audience. */
export type KnowledgeRolloutMode = "Off" | "Pilot" | "Everyone";

export const CONTENT_TYPE_LABELS: Record<ArticleContentType, string> = {
  HowTo: "How-to guide",
  // ⚠️ Labelled everywhere as AI-only, not just in the editor's own warning note (kb.manage.tsx)
  // — this label is reused on the article list row and anywhere else CONTENT_TYPE_LABELS is
  // read, so an agent scanning a list sees the same fact without opening the article.
  Troubleshooting: "Troubleshooting (AI only)",
  Reference: "Reference",
};

export const STATUS_LABELS: Record<ArticleStatus, string> = {
  Draft: "Draft",
  InReview: "In review",
  Published: "Published",
  Archived: "Archived",
};

export const VISIBILITY_LABELS: Record<ArticleVisibility, string> = {
  Everyone: "Everyone",
  Audience: "A pilot group",
  DeskOnly: "The service desk only",
};

export interface KnowledgeCollection {
  id: string;
  slug: string;
  name: string;
  description: string | null;

  /**
   * ⚠️ Counted through the SAME readable set the list uses, so a shelf never advertises
   * articles the reader cannot open.
   */
  articleCount: number;
}

/** A row in a list — the library, a search result, related articles on the request form. */
export interface ArticleListItem {
  collectionSlug: string;
  collectionName: string;
  slug: string;
  title: string;
  summary: string | null;
  contentType: ArticleContentType;
  appliesTo: string | null;
  updatedAtUtc: string;
}

export interface KnowledgeHome {
  collections: KnowledgeCollection[];
  recentlyUpdated: ArticleListItem[];
}

/**
 * One article as a reader sees it.
 *
 * ⚠️ No author id and no author name. Who wrote a how-to guide is not a fact the reader needs,
 * and a name on every article is a person-descriptor on a page ~2,600 people can open. The
 * owning TEAM travels instead — that is who maintains it and who a correction goes to.
 */
export interface ArticleDetail {
  collectionSlug: string;
  collectionName: string;
  slug: string;
  title: string;
  summary: string | null;
  body: string;
  contentType: ArticleContentType;
  appliesTo: string | null;
  owningTeamName: string;
  updatedAtUtc: string;
  publishedAtUtc: string | null;
  lastReviewedAtUtc: string | null;
}

export interface ArticleAudienceMember {
  kind: ArticleAudienceKind;
  subjectId: string;
}

/** An article as its author edits it. */
export interface AuthorArticle {
  id: string;
  collectionId: string;
  slug: string;
  title: string;
  summary: string | null;
  body: string;
  contentType: ArticleContentType;
  status: ArticleStatus;
  visibility: ArticleVisibility;
  owningTeamId: string;
  owningTeamName: string;
  appliesTo: string | null;
  keywords: string | null;
  categoryIds: string[];
  audience: ArticleAudienceMember[];
  updatedAtUtc: string;
  publishedAtUtc: string | null;
  lastReviewedAtUtc: string | null;
  reviewDueAtUtc: string | null;

  /**
   * ⚠️ Rendering only, and a DIFFERENT question from having reached the screen: the group gate
   * is an any-of over three permissions, and which of them somebody holds decides what they may
   * do to THIS article. The publish endpoint re-checks and answers 404.
   */
  canPublish: boolean;

  /** Null on the list — see the endpoint. Present when one article is opened. */
  health: ArticleHealth | null;
}

/**
 * Why somebody said an article did not help.
 *
 * ⚠️ **A closed set and never a text box** — D-63's reasoning verbatim: "how could this have been
 * better?" on a healthcare desk invites exactly the clinical detail the field would then have to
 * be governed as. A checkbox cannot name a patient. Every member describes the ARTICLE, never a
 * person (D-24).
 */
export type ArticleFeedbackReason =
  "NotGiven" | "OutOfDate" | "DidNotMatch" | "StepsDidNotWork" | "HardToFollow" | "Incomplete";

export const FEEDBACK_REASON_LABELS: Record<ArticleFeedbackReason, string> = {
  NotGiven: "I would rather not say",
  OutOfDate: "It is out of date",
  DidNotMatch: "It did not match my situation",
  StepsDidNotWork: "I followed it and it did not work",
  HardToFollow: "It was hard to follow",
  Incomplete: "The answer I needed was not in it",
};

/** The reasons actually offered, in the order they are drawn. `NotGiven` is the absence of one. */
export const FEEDBACK_REASONS: ArticleFeedbackReason[] = [
  "OutOfDate",
  "DidNotMatch",
  "StepsDidNotWork",
  "HardToFollow",
  "Incomplete",
];

/**
 * How an article is doing, for the people who maintain it.
 *
 * ⚠️ **Counts, and not one of them names anybody.** There is no reader list and no way to ask
 * for one — the tables behind these have no user column at all, which is the refusal D-69,
 * D-70, D-108, D-118 and D-119 each made, held by the shape of the data rather than by this
 * type choosing not to carry it.
 *
 * ⚠️ `helpfulRate` is **null below five answers**, with the counts still exact — "100% helpful"
 * over one answer is a ranking by sample size (D-130).
 *
 * ⚠️ `deflectionShownCount`/`deflectionAnsweredCount` (D-174) are the same shape — counts, no
 * user column behind either. This is the reporting surface for the semantic-deflection feature;
 * there is no separate `/reports/knowledge` screen (D-170).
 */
export interface ArticleHealth {
  views30: number;
  helpful: number;
  notHelpful: number;
  helpfulRate: number | null;
  reasons: { reason: ArticleFeedbackReason; count: number }[];
  deflectionShownCount: number;
  deflectionAnsweredCount: number;
}

export interface ArticleVersion {
  number: number;
  title: string;
  summary: string | null;
  body: string;
  publishedAtUtc: string;
}

/**
 * Somebody in the pilot.
 *
 * ⚠️ `isKnown` false is an id with no account behind it — still listed, never dropped, because
 * it is still in the pilot and somebody has to be able to take it out.
 */
export interface KnowledgeRolloutMember {
  userId: string;
  displayName: string;
  jobTitle: string | null;
  isKnown: boolean;
  isActive: boolean;
}

export interface KnowledgeRollout {
  mode: KnowledgeRolloutMode;
  members: KnowledgeRolloutMember[];
  updatedAtUtc: string;
}

export interface SaveArticleInput {
  title: string;
  summary: string | null;
  body: string;
  contentType: ArticleContentType;
  visibility: ArticleVisibility;
  collectionId: string;
  owningTeamId: string;
  appliesTo: string | null;
  keywords: string | null;
  categoryIds: string[];
  audience: ArticleAudienceMember[];
}

// ---------------------------------------------------------------------------
// Reader
// ---------------------------------------------------------------------------

export interface KnowledgeCollectionContents {
  collection: KnowledgeCollection;
  articles: ArticleListItem[];
}

export const getKnowledgeHome = () => api<KnowledgeHome>("/api/kb");

/** One collection and its table of contents — the left rail, and where a library card lands. */
export const getCollection = (slug: string) =>
  api<KnowledgeCollectionContents>(`/api/kb/${encodeURIComponent(slug)}`);

/**
 * ⚠️ Three characters minimum, enforced by the server, which REFUSES a shorter term with a
 * message rather than dropping it — ignoring it would return the whole library under a heading
 * saying these are the matches.
 */
export const searchArticles = (q: string) =>
  api<ArticleListItem[]>(`/api/kb/search?q=${encodeURIComponent(q)}`);

export const getArticle = (collection: string, slug: string) =>
  api<ArticleDetail>(`/api/kb/${encodeURIComponent(collection)}/${encodeURIComponent(slug)}`);

/**
 * Articles worth reading before raising a request in this category.
 *
 * ⚠️ Answers an EMPTY LIST rather than an error when the knowledge base is off or out of
 * reach. This is a courtesy on somebody else's screen, and a red box on the intake form for
 * advice that was unavailable would stop people raising the request they came for (D-160).
 */
export const getRelatedArticles = (categoryId: string) =>
  api<ArticleListItem[]>(`/api/kb/related?categoryId=${encodeURIComponent(categoryId)}`);

/**
 * Semantic deflection (D-174): articles ranked by how closely they match what has been typed
 * so far, from an embedding of the text rather than a category tag.
 *
 * ⚠️ Locked behind `ai.kb-deflection` until the abuse-monitoring exemption is granted — a 404
 * here means the feature is off, unconfigured, or the library does not reach this reader, the
 * same convention every other AI endpoint in this product follows. Also answers an EMPTY LIST
 * (never an error) when there is too little text or nothing scored above the relevance floor —
 * this is a courtesy on the new-request form, same reasoning as `getRelatedArticles`.
 */
export function suggestArticles(text: string): Promise<ArticleListItem[]> {
  return api<{ articles: ArticleListItem[] }>("/api/kb/deflection/suggest", {
    method: "POST",
    body: { text },
  }).then((r) => r.articles);
}

/** "This answered it" — a count, and nothing about who pressed it or what they typed (D-160's shape). */
export function recordDeflectionAnswered(
  collectionSlug: string,
  articleSlug: string,
): Promise<void> {
  return api<void>(
    `/api/kb/${encodeURIComponent(collectionSlug)}/${encodeURIComponent(articleSlug)}/deflection-answered`,
    { method: "POST" },
  );
}

// ---------------------------------------------------------------------------
// Authoring
// ---------------------------------------------------------------------------

export const listAuthorArticles = () => api<AuthorArticle[]>("/api/kb/manage/articles");

export const getAuthorArticle = (id: string) => api<AuthorArticle>(`/api/kb/manage/articles/${id}`);

export const createArticle = (input: {
  collectionId: string;
  owningTeamId: string;
  title: string;
  contentType: ArticleContentType;
}) => api<AuthorArticle>("/api/kb/manage/articles", { method: "POST", body: input });

export const saveArticle = (id: string, input: SaveArticleInput) =>
  api<AuthorArticle>(`/api/kb/manage/articles/${id}`, { method: "PUT", body: input });

/**
 * ⚠️ Its own call under its own permission, never a field on the save.
 *
 * A body that could carry a status would make "publish this to the organisation" reachable as
 * a side effect of saving a typo fix — so the thing that must not happen is unrepresentable
 * rather than refused.
 */
export const publishArticle = (id: string) =>
  api<void>(`/api/kb/manage/articles/${id}/publish`, { method: "POST" });

export const archiveArticle = (id: string) =>
  api<void>(`/api/kb/manage/articles/${id}/archive`, { method: "POST" });

/** Records that somebody checked the article is still true, without republishing it. */
export const markArticleReviewed = (id: string) =>
  api<void>(`/api/kb/manage/articles/${id}/reviewed`, { method: "POST" });

export const deleteArticle = (id: string) =>
  api<void>(`/api/kb/manage/articles/${id}`, { method: "DELETE" });

/**
 * "Did this help?"
 *
 * ⚠️ **Nothing records who answered.** The row has no user column, so this is a signal rather
 * than a ballot: one person can answer twice and on a rarely-read article that moves the number.
 * The alternative buys accuracy on a satisfaction figure and pays for it with a permanent record
 * of which employee found which instructions confusing — see `ArticleFeedback` in the domain.
 *
 * ⚠️ Answers 204. Returning the running total would turn one person's answer into a live tally
 * of their colleagues' opinions, on a page anybody can open.
 */
export const sendArticleFeedback = (
  collection: string,
  slug: string,
  input: { helpful: boolean; reason: ArticleFeedbackReason | null },
) =>
  api<void>(`/api/kb/${encodeURIComponent(collection)}/${encodeURIComponent(slug)}/feedback`, {
    method: "POST",
    body: input,
  });

/**
 * Puts an earlier version's words back into the draft.
 *
 * ⚠️ It restores the TEXT and publishes nothing — rolling back is two deliberate acts, because
 * an undo that silently republished is the shape where a mis-click puts month-old instructions
 * in front of everybody.
 */
export const restoreArticleVersion = (id: string, number: number) =>
  api<AuthorArticle>(`/api/kb/manage/articles/${id}/versions/${number}/restore`, {
    method: "POST",
  });

export const getArticleVersions = (id: string) =>
  api<ArticleVersion[]>(`/api/kb/manage/articles/${id}/versions`);

// ---------------------------------------------------------------------------
// Rollout
// ---------------------------------------------------------------------------

export const getKnowledgeRollout = () => api<KnowledgeRollout>("/api/kb/manage/rollout");

/**
 * ⚠️ `kb.manage` alone, not the authoring any-of. Deciding how many people have been told a
 * product exists is administration of the library rather than authorship in it.
 */
export const setKnowledgeRollout = (input: {
  mode: KnowledgeRolloutMode;
  memberUserIds: string[];
}) => api<KnowledgeRollout>("/api/kb/manage/rollout", { method: "PUT", body: input });

// ---------------------------------------------------------------------------
// The shelves
// ---------------------------------------------------------------------------

/**
 * A collection as its administrator sees it.
 *
 * ⚠️ Two counts, because one would be a figure nobody could act on: `articleCount` is
 * everything on the shelf including drafts, `publishedCount` is what a reader can reach. A
 * shelf reading "6 articles" that is empty to everybody else is the state this screen exists to
 * make visible.
 */
export interface KnowledgeCollectionAdmin {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  order: number;
  isActive: boolean;
  articleCount: number;
  publishedCount: number;
}

export const listManagedCollections = () =>
  api<KnowledgeCollectionAdmin[]>("/api/kb/manage/collections");

export const createManagedCollection = (input: {
  name: string;
  description: string | null;
  order: number | null;
}) => api<KnowledgeCollectionAdmin>("/api/kb/manage/collections", { method: "POST", body: input });

/**
 * ⚠️ There is no `slug` here and there is none on the server's command either. A slug is
 * derived once at creation and never regenerated on rename, so a field for it would be a way to
 * repoint every link somebody has pasted into a request.
 */
export const renameManagedCollection = (
  id: string,
  input: { name: string; description: string | null; order: number | null },
) =>
  api<KnowledgeCollectionAdmin>(`/api/kb/manage/collections/${id}`, {
    method: "PUT",
    body: input,
  });

/**
 * ⚠️ Two calls, not one taking a boolean (D-113): an absent JSON boolean binds as false, so a
 * single endpoint would make hiding a shelf the reading a truncated body falls into.
 */
export const setManagedCollectionActive = (id: string, active: boolean) =>
  api<KnowledgeCollectionAdmin>(
    `/api/kb/manage/collections/${id}/${active ? "reactivate" : "deactivate"}`,
    { method: "POST" },
  );

// ---------------------------------------------------------------------------
// The assistant (D-5)
// ---------------------------------------------------------------------------

/**
 * What the assistant proposed.
 *
 * ⚠️ Shaped as the five editable fields so the screen can offer each one individually. An author
 * who likes the body and not the title should not have to take both.
 *
 * ⚠️ `body` is markdown, and it goes through `parseArticle` like every other body. That parser is
 * total — anything it does not recognise comes out as visible text, and a link whose target is
 * not `https://` or an internal path is drawn as its label alone — so nothing a model can emit
 * becomes markup. The `dangerouslySetInnerHTML` ban and the enforcing CSP are untouched by this
 * feature, and no sanitiser was added for it.
 */
export interface ArticleAssistResult {
  title: string;
  summary: string;
  body: string;
  keywords: string;
  appliesTo: string;
}

/**
 * ⚠️ 404 means the assistant is not available here — the knowledge base is off, `kb.ai-authoring`
 * is off (its state on every deployment until the Azure OpenAI exemption is granted), or no model
 * is configured. `canUseKnowledgeAssistant` on the session is what decides whether the control is
 * drawn; this is what decides whether it answers.
 *
 * ⚠️ A 503 means the model came back with nothing usable and NOTHING WAS SAVED. It is not the
 * same answer as a 404 and the screen must not collapse the two (D-122).
 *
 * ⚠️ `referenceUrl` is NEVER fetched — by this client or by the server. It travels as plain text
 * the model is told it cannot open, the same way `instruction` does; a 400 here means it did not
 * even look like a web address.
 */
export const assistArticle = (input: {
  instruction: string;
  contentType: ArticleContentType;
  referenceUrl: string | null;
  existing: string | null;
}) => api<ArticleAssistResult>("/api/kb/manage/assist", { method: "POST", body: input });
